import AlarmErrorWidgetDemo from "./components/ClientDashboardDemoComponents/AlarmErrorWidgetDemo.js";
import BatteryStatusWidgetDemo from "./components/ClientDashboardDemoComponents/BatteryStatusWidgetDemo.js";
import EnergyConsumptionChartDemo from "./components/ClientDashboardDemoComponents/EnergyConsumptionChartDemo.js"
// import EnergyTrendWidgetDemo from "./components/ClientDashboardDemoComponents/EnergyTrendWidgetDemo.js"
import WeeklyEnergyTrendCard from "./components/ClientDashboardDemoComponents/WeeklyEnergyTrendCard.js"
import LastBidWidgetDemo from "./components/ClientDashboardDemoComponents/LastBidWidgetDemo.js";
import LiveStatusTable from "./components/ClientDashboardDemoComponents/LiveStatusTable.js";
import PowerConsumptionChartDemo from "./components/ClientDashboardDemoComponents/PowerConsumptionChartDemo.js";
import WidgetDemo from "../../pages/tables/components/ClientDashboardDemoComponents/WidgetDemo.js";
import styles from "./ClientDashboardDemo.module.scss";
import { useSelector } from "react-redux";
import { useEffect, useRef, useCallback, useState } from "react";
import config from "../../config";
import CustomOnlyModal from "../../components/CustomModal/CustomOnlyModal.js";

export default function ClientDashboard() {
  const store = useSelector((state) => state.auth);
  const eventSourceRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [countdown, setCountdown] = useState("");
  const intervalId = useRef(null);

  const toggleAlertModal = useCallback(() => {
    clearTimer();
    setCountdown("");
    setAlertOpen((prev) => !prev);
  }, []);

  const clearTimer = () => {
    clearInterval(intervalId.current);
    intervalId.current = null;
  }

  // Function to establish SSE connection with retry logic
  const connectSSE = useCallback((retryCount = 0) => {
    // Clear any existing timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No authentication token found");
      return;
    }

    // Calculate retry delay with exponential backoff (max 30 seconds)
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 30000);
    
    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const eventSource = new EventSource(`${config.baseURLApi}/sse/events?token=${token}`);
      eventSourceRef.current = eventSource;
      eventSource.addEventListener("alert", (event) => {
        const data = JSON.parse(event.data);
        console.log(data);
        const activated = data.activated === true || String(data.activated).toLowerCase() === "true";
        const deactivated = data.deactivated === true || String(data.deactivated).toLowerCase() === "true";
        const now = new Date();
        if (activated && !intervalId.current && data.type == "IL") {
          intervalId.current = setInterval(() => {
            const targetTime = new Date(now.getTime() + 5 * 60000); // 5 minutes from now
            const currentTime = new Date();
            const diff = targetTime - currentTime;
            if (diff <= 0) {
              setCountdown("00:00");
              clearTimer();
              return;
            }
            const minutes = String(Math.floor(diff / 60000)).padStart(2, "0");
            const seconds = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
            setCountdown(`${minutes}:${seconds}`);
          }, 1000);
          setAlertMessage("Activate load curtailment within 5 minutes from " + now.toLocaleTimeString());
          setAlertOpen(true);
        } else if (deactivated && data.type=="IL") {
          setCountdown("Resume normal operations.");
          setAlertMessage("Restore load within 5 minutes from " + now.toLocaleTimeString());
          setAlertOpen(true);
          clearTimer();
        }
      });
      
      eventSource.onerror = (error) => {
        console.warn("SSE connection error:", error);
        eventSource.close();
        
        // Retry connection with exponential backoff
        retryTimeoutRef.current = setTimeout(() => {
          connectSSE(retryCount + 1);
        }, retryDelay);
      };
    } catch (error) {
      console.error("Failed to establish SSE connection:", error);
      // Retry connection with exponential backoff
      retryTimeoutRef.current = setTimeout(() => {
        connectSSE(retryCount + 1);
      }, retryDelay);
    }
  }, []);

  // Set up SSE connection
  useEffect(() => {
    connectSSE();

    return () => {
      // Clean up on unmount
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [store, connectSSE]);

  if (!store?.currentUser?.clientRole?.role) {
    return null;
  }

  const clientRole = store.currentUser.clientRole.role;
  const facilityId = store.currentUser.clientRole.facility_id;
  const buildingId = store.currentUser.clientRole.building_id;
  const hasBattery = store.currentUser.clientRole.building?.battery_table !== null;
  //const buildingName = store.currentUser.clientRole.building.name;
  //const selectedAsset = facilityName;
  // const demoFacilityName = "Asiagreen" //placeholder as SICC doesn't have registered name
  //console.log(buildingName);
  return (
    <div className={styles.ClientDashboardDemoPage}>
      <CustomOnlyModal
        modalStatus={alertOpen}
        toggle={toggleAlertModal}
        size="lg"
        modalTitle="Alert"
      >
        <div style={{ fontSize: "28px", color: "red", fontWeight: 700, textAlign: "center" }}>
          {countdown}
        </div>
        <div style={{ fontSize: "16px", color: "grey", textAlign: "center" }}>
          {alertMessage}
        </div>
      </CustomOnlyModal>
      <div className={styles.WidgetRow} style={{ flex: 1 }}>
        <div className={styles.WidgetHalfWidthContainer}>
          <div className={styles.WidgetSideBySideContainer}>
            <div className={styles.WidgetSideBySideItem}>
              <WidgetDemo title="Power">
                <PowerConsumptionChartDemo 
                  buildingId={buildingId} 
                  userRole={clientRole} 
                />
              </WidgetDemo>
            </div>
            <div className={styles.WidgetSideBySideItem}>
              <WidgetDemo title="Energy">
                <EnergyConsumptionChartDemo buildingId={buildingId} />
              </WidgetDemo>
            </div>
          </div>
        </div>
        <div className={styles.WidgetHalfWidthContainer}>
          <div className={styles.WidgetSideBySideContainer}>
            <div className={styles.WidgetSideBySideItem}>
            <WidgetDemo title="Last Cleared DR/IL">
                <LastBidWidgetDemo facilityId={facilityId}/>
              </WidgetDemo>
            </div>
          </div>
      </div>
      </div>

      <div className={styles.WidgetRow} style={{ flex: 4 }}>
        <div className={styles.WidgetHalfWidthContainer}>
          <div className={styles.WidgetSideBySideContainer}>
            <div className={styles.WidgetSideBySideItem}>
              <WidgetDemo title="Energy Trend">
                <WeeklyEnergyTrendCard buildingId={buildingId} />
              </WidgetDemo>
            </div>
            <div className={styles.WidgetSideBySideItem}>
                <WidgetDemo title="Live Status">
                  <LiveStatusTable buildingId={buildingId} />
                </WidgetDemo>
            </div>
          </div>
        </div>
        <div className={styles.WidgetHalfWidthContainer}>
          {hasBattery && (
            <div className={styles.WidgetSideBySideContainer}>
              <div className={styles.WidgetSideBySideItem} style={{ minHeight: "60vh" }}>
                <BatteryStatusWidgetDemo buildingId={buildingId} />
                
              </div>
              <div className={styles.WidgetSideBySideItem} style={{ minHeight: "60vh" }}>
                <WidgetDemo title="Alarm Status">
                  <AlarmErrorWidgetDemo buildingId={buildingId} />
                </WidgetDemo>
              </div>
            </div>
          )}
          {!hasBattery && <div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Battery Data Not Available</div>}
        </div>
      </div>
    </div>
  );
}
